/* eslint-disable max-lines */
/* eslint-disable max-params */
/* eslint-disable no-continue */
/**
 * Negative Space Processor Module
 * Removes pixels similar to t-shirt color using CIELAB + CIEDE2000 color distance
 */

// ========== TypeScript Interfaces ==========

export interface NegativeSpaceParams {
  imageData: ImageData;
  targetHexColor: string;
}

export interface NegativeSpaceResult {
  imageData: ImageData;
  stats: {
    removedPixelCount: number;
    eligiblePixelCount: number;
    targetColorLab: { L: number; a: number; b: number };
    effectiveTargetLab: { L: number; a: number; b: number };
    effectiveThreshold: number;
    wasColorSnapped: boolean;
    snapDistance?: number;
    usedFallbackProbe?: boolean;
  };
}

// Internal types
interface LabColor {
  L: number;
  a: number;
  b: number;
}

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

interface XyzColor {
  X: number;
  Y: number;
  Z: number;
}

// ========== Internal Configuration ==========

// Calculated dynamically based on shirt color
const calculateAdaptiveParams = (lab: LabColor) => ({
  baseToleranceΔE00: lerp(6, 10, lab.L / 100),
  featherWidthΔE00: lerp(1.0, 1.5, lab.L / 100),
  minAreaPixels: Math.round(lerp(80, 120, lab.L / 100))
});

// Easily modifiable for debugging
const PROCESSING_CONFIG = {
  ignoreTransparent: true,
  enableMorphology: false,
  enableFeathering: true,
  morphologyRadius: 1,
  enableColorSnapping: true,
  maxSnapDistance: 18,
  minSnapArea: 500,
  enableFallbackProbe: true,
  probeExtension: 2,
  useClusteringMethod: false,
  maxSampleSize: 100000,
  nearTargetThresholdE76: 10,
  estimatorMinPixels: 500,
  consoleLog: false
};

// ========== Utility Functions ==========

// clamp function removed - not used in current implementation

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function formatLab(lab: LabColor): string {
  return `lab(${lab.L.toFixed(2)}, ${lab.a.toFixed(2)}, ${lab.b.toFixed(2)})`;
}

function formatDominantColors(
  dominantColors: ColorCluster[]
): Array<{ lab: string; percentage: string }> {
  return dominantColors.map(cluster => ({
    lab: formatLab(cluster.centroidLab),
    percentage: `${cluster.percentage.toFixed(2)}%`
  }));
}

// ========== Color Parsing and Conversion ==========

function parseHexColor(hex: string): RgbColor {
  let s = hex.trim();
  if (s[0] !== "#") s = `#${s}`;
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) {
    throw new Error("Invalid hex color");
  }
  if (s.length === 4) {
    const r = s[1];
    const g = s[2];
    const b = s[3];
    s = `#${r}${r}${g}${g}${b}${b}`;
  }
  const r = parseInt(s.slice(1, 3), 16);
  const g = parseInt(s.slice(3, 5), 16);
  const b = parseInt(s.slice(5, 7), 16);
  return { r, g, b };
}

// sRGB (0..255) -> linear 0..1
function srgb2lin(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

// sRGB -> XYZ (D65), normalized 0..1
function rgb2xyz(r: number, g: number, b: number): XyzColor {
  const R = srgb2lin(r);
  const G = srgb2lin(g);
  const B = srgb2lin(b);
  // sRGB D65 conversion matrix
  const X = R * 0.4124564 + G * 0.3575761 + B * 0.1804375;
  const Y = R * 0.2126729 + G * 0.7151522 + B * 0.072175;
  const Z = R * 0.0193339 + G * 0.119192 + B * 0.9503041;
  return { X, Y, Z };
}

// XYZ -> Lab (D65)
function xyz2lab(X: number, Y: number, Z: number): LabColor {
  // D65 white point (normalized)
  const Xn = 0.95047;
  const Yn = 1.0;
  const Zn = 1.08883;
  const x = X / Xn;
  const y = Y / Yn;
  const z = Z / Zn;
  const eps = 216 / 24389; // 0.008856
  const kappa = 24389 / 27; // 903.3

  const fx = x > eps ? Math.cbrt(x) : (kappa * x + 16) / 116;
  const fy = y > eps ? Math.cbrt(y) : (kappa * y + 16) / 116;
  const fz = z > eps ? Math.cbrt(z) : (kappa * z + 16) / 116;

  const L = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);
  return { L, a, b };
}

function rgb2lab(r: number, g: number, b: number): LabColor {
  const { X, Y, Z } = rgb2xyz(r, g, b);
  return xyz2lab(X, Y, Z);
}

// ========== Color Distance Calculation ==========

// CIE76 - Simple Euclidean distance in Lab space (faster than CIEDE2000)
function deltaE76(lab1: LabColor, lab2: LabColor): number {
  const dL = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

// CIEDE2000 (kL=kC=kH=1)
function deltaE2000(lab1: LabColor, lab2: LabColor): number {
  const L1 = lab1.L;
  const a1 = lab1.a;
  const b1 = lab1.b;
  const L2 = lab2.L;
  const a2 = lab2.a;
  const b2 = lab2.b;

  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cbar = (C1 + C2) / 2;

  const Cbar7 = Cbar ** 7;
  const G = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + 25 ** 7)));

  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);

  const h1p = (Math.atan2(b1, a1p) * 180) / Math.PI + (Math.atan2(b1, a1p) < 0 ? 360 : 0);
  const h2p = (Math.atan2(b2, a2p) * 180) / Math.PI + (Math.atan2(b2, a2p) < 0 ? 360 : 0);

  const dLp = L2 - L1;
  const dCp = C2p - C1p;

  let dhp: number;
  if (C1p * C2p === 0) {
    dhp = 0;
  } else {
    const diff = h2p - h1p;
    if (Math.abs(diff) <= 180) dhp = diff;
    else if (diff > 180) dhp = diff - 360;
    else dhp = diff + 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * Math.PI) / 360);

  const Lp = (L1 + L2) / 2;
  const Cp = (C1p + C2p) / 2;

  let hp: number;
  if (C1p * C2p === 0) {
    hp = h1p + h2p;
  } else {
    const sum = h1p + h2p;
    const diff = Math.abs(h1p - h2p);
    if (diff <= 180) hp = sum / 2;
    else hp = (sum + (sum < 360 ? 360 : -360)) / 2;
  }

  const T =
    1 -
    0.17 * Math.cos(((hp - 30) * Math.PI) / 180) +
    0.24 * Math.cos((2 * hp * Math.PI) / 180) +
    0.32 * Math.cos(((3 * hp + 6) * Math.PI) / 180) -
    0.2 * Math.cos(((4 * hp - 63) * Math.PI) / 180);

  const dTheta = 30 * Math.exp(-(((hp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cp ** 7 / (Cp ** 7 + 25 ** 7));
  const Rt = -Rc * Math.sin((2 * dTheta * Math.PI) / 180);

  const Sl = 1 + (0.015 * (Lp - 50) ** 2) / Math.sqrt(20 + (Lp - 50) ** 2);
  const Sc = 1 + 0.045 * Cp;
  const Sh = 1 + 0.015 * Cp * T;

  const kl = 1;
  const kc = 1;
  const kh = 1;

  const termL = dLp / (kl * Sl);
  const termC = dCp / (kc * Sc);
  const termH = dHp / (kh * Sh);

  return Math.sqrt(termL * termL + termC * termC + termH * termH + Rt * termC * termH);
}

// ========== Color Analysis Functions ==========

// Color clustering for finding dominant colors
interface ColorCluster {
  centroidLab: LabColor;
  pixelCount: number;
  percentage: number;
}

function findDominantColors(
  imageData: ImageData,
  maxClusters: number = 12,
  downsampleFactor: number = 4,
  printDuration: (message: string) => void = () => {}
): ColorCluster[] {
  const { width, height, data } = imageData;
  const clusters: ColorCluster[] = [];

  // Simple k-means clustering approach
  // Step 1: Collect sampled pixels
  const sampledPixels: LabColor[] = [];

  for (let y = 0; y < height; y += downsampleFactor) {
    for (let x = 0; x < width; x += downsampleFactor) {
      const idx = (y * width + x) * 4;
      const a = data[idx + 3];

      // Skip transparent pixels
      if (a === 0) continue;

      const r = data[idx] ?? 0;
      const g = data[idx + 1] ?? 0;
      const b = data[idx + 2] ?? 0;
      const lab = rgb2lab(r, g, b);
      sampledPixels.push(lab);
    }
  }

  printDuration("→ Collect sampled pixels");

  if (sampledPixels.length === 0) return clusters;

  // Step 2: Initialize cluster centers (k-means++)
  const centers: LabColor[] = [];

  // First center is random
  const firstPixel = sampledPixels[Math.floor(Math.random() * sampledPixels.length)];
  if (firstPixel) {
    centers.push(firstPixel);
  }

  // Select remaining centers using k-means++ method
  for (let k = 1; k < Math.min(maxClusters, sampledPixels.length); k += 1) {
    const distances = sampledPixels.map(pixel => {
      const minDist = Math.min(...centers.map(center => deltaE2000(pixel, center)));
      return minDist * minDist; // Square the distance for k-means++
    });

    // Pick new center with probability proportional to squared distance
    const totalDist = distances.reduce((sum, d) => sum + d, 0);
    let threshold = Math.random() * totalDist;
    let selectedIdx = 0;

    for (let i = 0; i < distances.length; i += 1) {
      const dist = distances[i];
      if (dist !== undefined) {
        threshold -= dist;
        if (threshold <= 0) {
          selectedIdx = i;
          break;
        }
      }
    }

    const selectedPixel = sampledPixels[selectedIdx];
    if (selectedPixel) {
      centers.push(selectedPixel);
    }
  }

  printDuration("→ Initialize cluster centers");

  // Step 3: Run k-means iterations
  const assignments = new Array<number>(sampledPixels.length);
  const clusterSums = new Array<{ L: number; a: number; b: number; count: number }>(centers.length);

  for (let iteration = 0; iteration < 10; iteration += 1) {
    // Reset cluster sums
    for (let k = 0; k < centers.length; k += 1) {
      clusterSums[k] = { L: 0, a: 0, b: 0, count: 0 };
    }

    // Assign pixels to nearest cluster
    for (let i = 0; i < sampledPixels.length; i += 1) {
      const pixel = sampledPixels[i];
      if (!pixel) continue;

      let minDist = Infinity;
      let bestCluster = 0;

      for (let k = 0; k < centers.length; k += 1) {
        const center = centers[k];
        if (!center) continue;

        const dist = deltaE2000(pixel, center);
        if (dist < minDist) {
          minDist = dist;
          bestCluster = k;
        }
      }

      assignments[i] = bestCluster;
      const clusterSum = clusterSums[bestCluster];
      if (clusterSum) {
        clusterSum.L += pixel.L;
        clusterSum.a += pixel.a;
        clusterSum.b += pixel.b;
        clusterSum.count += 1;
      }
    }

    // Update cluster centers
    let changed = false;
    for (let k = 0; k < centers.length; k += 1) {
      const clusterSum = clusterSums[k];
      const currentCenter = centers[k];

      if (clusterSum && currentCenter && clusterSum.count > 0) {
        const newCenter: LabColor = {
          L: clusterSum.L / clusterSum.count,
          a: clusterSum.a / clusterSum.count,
          b: clusterSum.b / clusterSum.count
        };

        if (deltaE2000(currentCenter, newCenter) > 0.1) {
          changed = true;
        }
        centers[k] = newCenter;
      }
    }

    // Early termination if converged
    if (!changed) break;
  }

  printDuration("→ Run k-means iterations");

  // Step 4: Count actual pixels per cluster (not just sampled)
  const pixelCounts = new Array<number>(centers.length).fill(0);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = (y * width + x) * 4;
      const a = data[idx + 3];

      if (a === 0) continue;

      const r = data[idx] ?? 0;
      const g = data[idx + 1] ?? 0;
      const b = data[idx + 2] ?? 0;
      const lab = rgb2lab(r, g, b);

      // Find nearest cluster
      let minDist = Infinity;
      let bestCluster = 0;

      for (let k = 0; k < centers.length; k += 1) {
        const center = centers[k];
        if (!center) continue;

        const dist = deltaE2000(lab, center);
        if (dist < minDist) {
          minDist = dist;
          bestCluster = k;
        }
      }

      const currentCount = pixelCounts[bestCluster] ?? 0;
      pixelCounts[bestCluster] = currentCount + 1;
    }
  }

  printDuration("→ Count actual pixels per cluster");

  // Step 5: Create final clusters with stats
  const totalCounted = pixelCounts.reduce((sum, count) => sum + count, 0);

  for (let k = 0; k < centers.length; k += 1) {
    const pixelCount = pixelCounts[k];
    const center = centers[k];

    if (pixelCount && pixelCount > 0 && center) {
      clusters.push({
        centroidLab: center,
        pixelCount,
        percentage: (pixelCount / totalCounted) * 100
      });
    }
  }

  // Sort by pixel count descending
  clusters.sort((a, b) => b.pixelCount - a.pixelCount);

  printDuration("→ Create and sort final clusters");

  return clusters;
}

// Find nearest color with constraints
function findNearestImageColor(
  targetLab: LabColor,
  clusters: ColorCluster[],
  maxDistance: number = 18,
  minArea: number = 500
): LabColor | null {
  let nearestCluster: ColorCluster | null = null;
  let minDist = Infinity;

  for (const cluster of clusters) {
    // Skip clusters that are too small
    if (cluster.pixelCount < minArea) continue;

    const dist = deltaE2000(targetLab, cluster.centroidLab);

    // Check if within max distance and closer than current best
    if (dist <= maxDistance && dist < minDist) {
      minDist = dist;
      nearestCluster = cluster;
    }
  }

  return nearestCluster ? nearestCluster.centroidLab : null;
}

// Fast near-target color estimation (alternative to clustering)
function findNearTargetColor(
  imageData: ImageData,
  targetLab: LabColor,
  maxSampleSize: number = 100000,
  thresholdE76: number = 10,
  minPixels: number = 500,
  printDuration: (message: string) => void = () => {}
): LabColor | null {
  const { width, height, data } = imageData;

  // Calculate adaptive sample stride
  const totalPixels = width * height;
  const sampleStride = Math.max(1, Math.ceil(Math.sqrt(totalPixels / maxSampleSize)));

  // Collect near-target pixels
  const nearTargetPixels: LabColor[] = [];
  let sampledCount = 0;

  for (let y = 0; y < height; y += sampleStride) {
    for (let x = 0; x < width; x += sampleStride) {
      const idx = (y * width + x) * 4;
      const a = data[idx + 3];

      // Skip transparent pixels
      if (a === 0) continue;

      sampledCount += 1;
      const r = data[idx] ?? 0;
      const g = data[idx + 1] ?? 0;
      const b = data[idx + 2] ?? 0;
      const lab = rgb2lab(r, g, b);

      // Use fast deltaE76 for filtering
      const distance = deltaE76(lab, targetLab);
      if (distance <= thresholdE76) {
        nearTargetPixels.push(lab);
      }
    }
  }

  printDuration("→ Fast near-target sampling");
  if (PROCESSING_CONFIG.consoleLog) {
    console.log(
      `[NSP] → Sampled ${sampledCount} pixels, found ${nearTargetPixels.length} near target`
    );
  }

  // Need minimum pixels for reliable estimate
  if (nearTargetPixels.length < minPixels) {
    return null;
  }

  // Calculate trimmed mean (remove outliers)
  const trimPercentage = 0.2; // Remove top/bottom 20%
  const trimCount = Math.floor(nearTargetPixels.length * trimPercentage);

  // Sort by lightness for trimming
  nearTargetPixels.sort((a, b) => a.L - b.L);

  // Calculate mean of middle portion
  let sumL = 0;
  let sumA = 0;
  let sumB = 0;
  let count = 0;

  for (let i = trimCount; i < nearTargetPixels.length - trimCount; i += 1) {
    const pixel = nearTargetPixels[i];
    if (pixel) {
      sumL += pixel.L;
      sumA += pixel.a;
      sumB += pixel.b;
      count += 1;
    }
  }

  if (count === 0) return null;

  const estimatedLab: LabColor = {
    L: sumL / count,
    a: sumA / count,
    b: sumB / count
  };

  if (PROCESSING_CONFIG.consoleLog) {
    console.log(`[NSP] → Estimated color: ${formatLab(estimatedLab)}`);
  }

  return estimatedLab;
}

// ========== Fallback Helper Functions ==========

interface PixelLab {
  lab: LabColor;
  x: number;
  y: number;
}

function findPixelsWithinThreshold(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  targetLab: LabColor,
  threshold: number,
  ignoreTransparent: boolean = true
): PixelLab[] {
  const pixels: PixelLab[] = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = (y * width + x) * 4;
      const a = data[idx + 3];

      if (ignoreTransparent && a === 0) continue;

      const r = data[idx] ?? 0;
      const g = data[idx + 1] ?? 0;
      const b = data[idx + 2] ?? 0;
      const lab = rgb2lab(r, g, b);
      const distance = deltaE2000(lab, targetLab);

      if (distance < threshold) {
        pixels.push({ lab, x, y });
      }
    }
  }

  return pixels;
}

function calculateMeanLab(pixels: PixelLab[]): LabColor {
  if (pixels.length === 0) {
    throw new Error("Cannot calculate mean of empty pixel array");
  }

  let totalL = 0;
  let totalA = 0;
  let totalB = 0;

  for (const pixel of pixels) {
    totalL += pixel.lab.L;
    totalA += pixel.lab.a;
    totalB += pixel.lab.b;
  }

  return {
    L: totalL / pixels.length,
    a: totalA / pixels.length,
    b: totalB / pixels.length
  };
}

// ========== Morphological Operations ==========

interface MorphologyParams {
  mask: Uint8Array;
  width: number;
  height: number;
  radius?: number;
}

function morphologicalErode(params: MorphologyParams): Uint8Array {
  const { mask, width, height, radius = 1 } = params;
  const result = new Uint8Array(mask);
  for (let y = radius; y < height - radius; y += 1) {
    for (let x = radius; x < width - radius; x += 1) {
      let allSet = true;
      for (let dy = -radius; dy <= radius && allSet; dy += 1) {
        for (let dx = -radius; dx <= radius && allSet; dx += 1) {
          if (mask[(y + dy) * width + (x + dx)] === 0) {
            allSet = false;
          }
        }
      }
      result[y * width + x] = allSet ? 1 : 0;
    }
  }
  return result;
}

function morphologicalDilate(params: MorphologyParams): Uint8Array {
  const { mask, width, height, radius = 1 } = params;
  const result = new Uint8Array(mask);
  for (let y = radius; y < height - radius; y += 1) {
    for (let x = radius; x < width - radius; x += 1) {
      let anySet = false;
      for (let dy = -radius; dy <= radius && !anySet; dy += 1) {
        for (let dx = -radius; dx <= radius && !anySet; dx += 1) {
          if (mask[(y + dy) * width + (x + dx)] === 1) {
            anySet = true;
          }
        }
      }
      result[y * width + x] = anySet ? 1 : 0;
    }
  }
  return result;
}

function morphologicalOpen(params: MorphologyParams): Uint8Array {
  const { mask, width, height, radius = 1 } = params;
  // Opening = erode then dilate (removes small objects)
  const eroded = morphologicalErode({ mask, width, height, radius });
  return morphologicalDilate({ mask: eroded, width, height, radius });
}

function morphologicalClose(params: MorphologyParams): Uint8Array {
  const { mask, width, height, radius = 1 } = params;
  // Closing = dilate then erode (fills small holes)
  const dilated = morphologicalDilate({ mask, width, height, radius });
  return morphologicalErode({ mask: dilated, width, height, radius });
}

// ========== Connected Component Analysis ==========

interface ComponentAnalysis {
  labels: Uint32Array;
  componentAreas: Map<number, number>;
}

function findConnectedComponents(
  mask: Uint8Array,
  width: number,
  height: number
): ComponentAnalysis {
  const labels = new Uint32Array(width * height);
  let nextLabel = 1;

  function floodFill(x: number, y: number, label: number): number {
    const stack: Array<[number, number]> = [[x, y]];
    let area = 0;

    while (stack.length > 0) {
      const [cx, cy] = stack.pop()!;
      if (cx < 0 || cx >= width || cy < 0 || cy >= height) continue;

      const idx = cy * width + cx;
      if (mask[idx] === 0 || labels[idx] !== 0) continue;

      labels[idx] = label;
      area += 1;

      stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
    }

    return area;
  }

  const componentAreas = new Map<number, number>();

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x;
      if (mask[idx] === 0 && labels[idx] === 0) {
        const area = floodFill(x, y, nextLabel);
        componentAreas.set(nextLabel, area);
        nextLabel += 1;
      }
    }
  }

  return { labels, componentAreas };
}

interface ComponentRemovalParams {
  mask: Uint8Array;
  width: number;
  minArea: number;
}

function removeSmallComponents(params: ComponentRemovalParams): Uint8Array {
  const { mask, width, minArea } = params;
  const height = mask.length / width;
  const { labels, componentAreas } = findConnectedComponents(mask, width, height);
  const result = new Uint8Array(mask);

  // Remove components smaller than minArea
  for (let i = 0; i < width * height; i += 1) {
    const label = labels[i];
    if (label !== undefined && label > 0) {
      const area = componentAreas.get(label);
      if (area !== undefined && area < minArea) {
        result[i] = 1; // Restore pixel (don't remove)
      }
    }
  }

  return result;
}

// ========== Main Processing Function ==========

// eslint-disable-next-line max-lines-per-function
export function processNegativeSpace(params: NegativeSpaceParams): NegativeSpaceResult {
  const startTime = performance.now();

  // Helper function to print duration with consistent formatting
  const printDuration = (message: string): void => {
    if (PROCESSING_CONFIG.consoleLog) {
      console.log(`[NSP] ${message}: ${((performance.now() - startTime) / 1000).toFixed(3)} s`);
    }
  };

  // 1. Validate inputs
  if (!params.imageData) {
    throw new Error("Invalid input: imageData is required");
  }
  if (!params.targetHexColor) {
    throw new Error("Invalid input: targetHexColor is required");
  }

  const { width, height, data } = params.imageData;
  if (width <= 0 || height <= 0) {
    throw new Error("Invalid input: image dimensions must be positive");
  }
  printDuration("Validate inputs");

  // 2. Parse hex color and convert to Lab
  let targetRgb: RgbColor;
  let targetLab: LabColor;
  try {
    targetRgb = parseHexColor(params.targetHexColor);
    targetLab = rgb2lab(targetRgb.r, targetRgb.g, targetRgb.b);
  } catch (error) {
    throw new Error(`Invalid hex color: ${params.targetHexColor}`);
  }
  printDuration("Parse hex color and convert to Lab");

  // 3. Calculate adaptive parameters internally
  const adaptiveParams = calculateAdaptiveParams(targetLab);
  const adjustedTolerance = adaptiveParams.baseToleranceΔE00;
  const featherWidthVal = adaptiveParams.featherWidthΔE00;
  const scaledMinArea = adaptiveParams.minAreaPixels;
  printDuration("Calculate adaptive parameters");

  // 3.5. Hybrid color selection
  let effectiveTargetLab = targetLab;
  let wasColorSnapped = false;
  let usedFallbackProbe = false;

  if (PROCESSING_CONFIG.enableColorSnapping) {
    let nearestColor: LabColor | null = null;

    if (PROCESSING_CONFIG.useClusteringMethod) {
      // Original clustering approach
      const dominantColors = findDominantColors(params.imageData, 12, 4, printDuration);
      nearestColor = findNearestImageColor(
        targetLab,
        dominantColors,
        PROCESSING_CONFIG.maxSnapDistance,
        PROCESSING_CONFIG.minSnapArea
      );

      if (PROCESSING_CONFIG.consoleLog) {
        console.log("[NSP] Using clustering method");
        console.log("[NSP] Dominant colors:", formatDominantColors(dominantColors));
        console.log("[NSP] Target lab:", formatLab(targetLab));
        console.log("[NSP] Nearest color:", nearestColor ? formatLab(nearestColor) : null);
      }
    } else {
      // Fast near-target estimation
      nearestColor = findNearTargetColor(
        params.imageData,
        targetLab,
        PROCESSING_CONFIG.maxSampleSize,
        PROCESSING_CONFIG.nearTargetThresholdE76,
        PROCESSING_CONFIG.estimatorMinPixels,
        printDuration
      );

      if (PROCESSING_CONFIG.consoleLog) {
        console.log("[NSP] Using fast estimator method");
        console.log("[NSP] Target lab:", formatLab(targetLab));
        console.log("[NSP] Estimated color:", nearestColor ? formatLab(nearestColor) : null);
      }
    }

    // Use nearest color if found, otherwise keep original
    if (nearestColor) {
      effectiveTargetLab = nearestColor;
      wasColorSnapped = true;
    }
  }
  printDuration("Hybrid color selection");

  // Get processing configuration
  const { ignoreTransparent, enableMorphology, enableFeathering, morphologyRadius } =
    PROCESSING_CONFIG;

  // 4. Create working copy of image data
  const outputImageData = new ImageData(new Uint8ClampedArray(data), width, height);
  const outputData = outputImageData.data;
  printDuration("Create working copy of image data");

  // 5. Process pixels (threshold, morphology, component removal, feathering)

  // Create binary mask (0 = remove, 1 = keep)
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i += 1) {
    mask[i] = 1; // Default to keep
  }

  // Compute distances and apply threshold
  let removedCount = 0;
  let eligibleCount = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x;
      const i = idx * 4;
      const a = outputData[i + 3];

      if (ignoreTransparent && a === 0) {
        mask[idx] = 0; // Already transparent
        continue;
      }

      eligibleCount += 1;
      const r = outputData[i] ?? 0;
      const g = outputData[i + 1] ?? 0;
      const b = outputData[i + 2] ?? 0;
      const lab = rgb2lab(r, g, b);
      const distance = deltaE2000(lab, effectiveTargetLab);

      if (distance < adjustedTolerance) {
        mask[idx] = 0; // Mark for removal
        removedCount += 1;
      }
    }
  }
  printDuration("Compute distances and apply threshold");

  // Fallback logic: if no color snapped and very few pixels removed
  if (
    PROCESSING_CONFIG.enableFallbackProbe &&
    !wasColorSnapped &&
    removedCount < eligibleCount * 0.002
  ) {
    // Try probe approach: find pixels within extended threshold
    const probeThreshold = adjustedTolerance + PROCESSING_CONFIG.probeExtension;
    const nearbyPixels = findPixelsWithinThreshold(
      outputData,
      width,
      height,
      targetLab,
      probeThreshold,
      ignoreTransparent
    );

    if (nearbyPixels.length >= PROCESSING_CONFIG.minSnapArea) {
      // Use mean of nearby pixels as new target
      effectiveTargetLab = calculateMeanLab(nearbyPixels);
      usedFallbackProbe = true;

      // Re-run threshold pass with new target
      removedCount = 0;
      // Reset mask to all keep
      for (let i = 0; i < width * height; i += 1) {
        mask[i] = 1;
      }

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const idx = y * width + x;
          const i = idx * 4;
          const a = outputData[i + 3];

          if (ignoreTransparent && a === 0) {
            mask[idx] = 0; // Already transparent
            continue;
          }

          const r = outputData[i] ?? 0;
          const g = outputData[i + 1] ?? 0;
          const b = outputData[i + 2] ?? 0;
          const lab = rgb2lab(r, g, b);
          const distance = deltaE2000(lab, effectiveTargetLab);

          if (distance < adjustedTolerance) {
            mask[idx] = 0; // Mark for removal
            removedCount += 1;
          }
        }
      }
    }
  }
  printDuration("Fallback logic");

  // Apply morphological operations if enabled
  let processedMask: Uint8Array = mask;
  if (enableMorphology) {
    // Opening to remove salt-and-pepper noise
    const openedMask = morphologicalOpen({ mask, width, height, radius: morphologyRadius });
    // Optional closing to fill small holes
    processedMask = morphologicalClose({
      mask: openedMask,
      width,
      height,
      radius: morphologyRadius
    });
  }
  printDuration("Apply morphological operations");

  // Remove small components
  processedMask = removeSmallComponents({ mask: processedMask, width, minArea: scaledMinArea });
  printDuration("Remove small components");

  // Apply feathering if enabled
  if (enableFeathering && featherWidthVal > 0) {
    // Create feathered alpha values
    const alphaMap = new Float32Array(width * height);

    // First pass: find edge pixels
    for (let y = 1; y < height - 1; y += 1) {
      for (let x = 1; x < width - 1; x += 1) {
        const idx = y * width + x;
        if (processedMask[idx] === 0) {
          // Check if this is an edge pixel
          let isEdge = false;
          for (let dy = -1; dy <= 1; dy += 1) {
            for (let dx = -1; dx <= 1; dx += 1) {
              if (dx === 0 && dy === 0) continue;
              if (processedMask[(y + dy) * width + (x + dx)] === 1) {
                isEdge = true;
                break;
              }
            }
            if (isEdge) break;
          }

          if (isEdge) {
            // Calculate distance to nearest kept pixel for feathering
            const r = outputData[idx * 4] ?? 0;
            const g = outputData[idx * 4 + 1] ?? 0;
            const b = outputData[idx * 4 + 2] ?? 0;
            const pixelLab = rgb2lab(r, g, b);
            const dist = deltaE2000(pixelLab, effectiveTargetLab);
            const t = Math.max(0, Math.min(1, (adjustedTolerance - dist) / featherWidthVal));
            alphaMap[idx] = 1 - t; // Smooth transition
          } else {
            alphaMap[idx] = 0; // Fully removed
          }
        } else {
          alphaMap[idx] = 1; // Fully kept
        }
      }
    }

    // Apply feathered alpha
    for (let i = 0; i < width * height; i += 1) {
      if (processedMask[i] === 0) {
        const currentAlpha = outputData[i * 4 + 3] ?? 255;
        const alphaMultiplier = alphaMap[i] ?? 1;
        outputData[i * 4 + 3] = Math.round(currentAlpha * alphaMultiplier);
      }
    }
  } else {
    // No feathering - apply hard mask
    for (let i = 0; i < width * height; i += 1) {
      if (processedMask[i] === 0) {
        outputData[i * 4 + 3] = 0;
      }
    }
  }
  printDuration("Apply feathering");

  // Count final removed pixels
  let finalRemoved = 0;
  for (let i = 0; i < width * height; i += 1) {
    if (processedMask[i] === 0) finalRemoved += 1;
  }
  printDuration("Count final removed pixels");

  // 6. Return processed image and stats
  const stats: NegativeSpaceResult["stats"] = {
    removedPixelCount: finalRemoved,
    eligiblePixelCount: eligibleCount,
    targetColorLab: targetLab,
    effectiveThreshold: adjustedTolerance,
    effectiveTargetLab,
    wasColorSnapped
  };

  // Calculate snap distance if color was modified
  if (wasColorSnapped || usedFallbackProbe) {
    stats.snapDistance = deltaE2000(targetLab, effectiveTargetLab);
  }

  // Include fallback probe info if used
  if (usedFallbackProbe) {
    stats.usedFallbackProbe = true;
  }

  printDuration("Total processing time");

  return {
    imageData: outputImageData,
    stats
  };
}
